/*
 * Copyright (C) 2020 Graylog, Inc.
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the Server Side Public License, version 1,
 * as published by MongoDB, Inc.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * Server Side Public License for more details.
 *
 * You should have received a copy of the Server Side Public License
 * along with this program. If not, see
 * <http://www.mongodb.com/licensing/server-side-public-license>.
 */
import { qualifyUrl } from 'util/URLUtils';
import UserNotification from 'util/UserNotification';

import Reflux from 'reflux';
import pull from 'lodash/pull';

import Store from 'logic/local-storage/Store';
import { Builder } from 'logic/rest/FetchProvider';
import CustomersActions from 'actions/customers/CustomersActions';
import { singletonStore } from 'logic/singleton';
import { SessionActions } from '../sessions/SessionStore';

export type Customer = {
  id: string,
  name: string,
};

type Callback = {
  (): void,
};

type CustomerSummaryResponse = {
  data: Array<Customer>,
};

const CustomersStore = singletonStore('Customers', () => Reflux.createStore({
  listenables: [CustomersActions],

  callbacks: [],

  listCustomers() {
    const accessToken = Store.get('access-token');

    if (accessToken && accessToken.length > 0) {
      const builder = new Builder('GET', qualifyUrl('/customers'));
      const promise = builder
        .setHeader('Accept', 'application/json')
        .setHeader('Authorization', `Bearer ${accessToken}`)
        .build()
        .then((response) => {
          if (!response.ok && response.status === 401) {
            SessionActions.logout();
          }

          return response.json();
        })
        .then((result: CustomerSummaryResponse) => {
          return result.data;
        })
        .catch((errorThrown) => {
          UserNotification.error(`Loading Customers failed with status: ${errorThrown}`,
            'Could not load Customers');
        });

      CustomersActions.listCustomers.promise(promise);

      return promise;
    }

    return Promise.resolve([]);
  },
  load(callback: ((streams: Array<Customer>) => void)) {
    this.listCustomers()
      .then((customers) => {
        callback(customers);
      });
  },
  onChange(callback: Callback) {
    this.callbacks.push(callback);
  },
  _emitChange() {
    this.callbacks.forEach((callback) => callback());
  },
  unregister(callback: Callback) {
    pull(this.callbacks, callback);
  },
}));

export { CustomersStore, CustomersActions };
export default CustomersStore;
